import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Check } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';

export interface NotificationListItem {
  key: string;
  timestamp: string;
  node: ReactNode;
}

type FilterMode = 'all' | 'unread';

interface NotificationListProps {
  items: NotificationListItem[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onMarkAllAsRead: () => void;
  isMarkAllPending: boolean;
  filter: FilterMode;
  onFilterChange: (f: FilterMode) => void;
}

export function NotificationList({
  items,
  isLoading,
  hasMore,
  onLoadMore,
  onMarkAllAsRead,
  isMarkAllPending,
  filter,
  onFilterChange,
}: NotificationListProps) {
  const { t } = useTranslation('notifications');

  return (
    <div data-testid="notifications-panel" className="flex flex-col w-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-1">
          <Button
            data-testid="notifications-filter-unread"
            variant={filter === 'unread' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => onFilterChange('unread')}
          >
            {t('panel.filterUnread')}
          </Button>
          <Button
            data-testid="notifications-filter-all"
            variant={filter === 'all' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => onFilterChange('all')}
          >
            {t('panel.filterAll')}
          </Button>
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              data-testid="notifications-mark-all-btn"
              variant="ghost"
              size="icon"
              aria-label={t('panel.markAllRead')}
              disabled={isMarkAllPending}
              aria-busy={isMarkAllPending}
              onClick={onMarkAllAsRead}
            >
              <Check className="h-4 w-4" aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('panel.markAllRead')}</TooltipContent>
        </Tooltip>
      </div>

      <div className="flex-1 overflow-y-auto max-h-[400px]">
        {isLoading && (
          <div
            data-testid="notifications-list-loading"
            className="flex items-center justify-center py-10 text-sm text-muted-foreground"
            aria-busy="true"
          >
            {t('panel.loading')}
          </div>
        )}

        {!isLoading && items.length === 0 && (
          <div
            data-testid="notifications-empty"
            className="flex flex-col items-center justify-center py-10 gap-2 text-sm text-muted-foreground"
          >
            <Bell className="h-8 w-8 opacity-30" aria-hidden="true" />
            <span>{t('panel.empty')}</span>
          </div>
        )}

        {!isLoading && items.length > 0 && (
          <div
            data-testid="notifications-list"
            className="flex flex-col divide-y divide-border"
            role="list"
          >
            {items.map((item) => (
              <div key={item.key} role="listitem">
                {item.node}
              </div>
            ))}
          </div>
        )}

        {hasMore && !isLoading && (
          <div className="flex justify-center py-3">
            <Button
              data-testid="notifications-load-more-btn"
              variant="ghost"
              size="sm"
              onClick={onLoadMore}
            >
              {t('panel.loadMore')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
